import React, {
  Suspense,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  lazy,
} from 'react'
import { useNavigate } from 'react-router-dom'

import {
  getOrgVenues,
  createPackage,
  getSeasons,
  createOrEditFlexPack,
} from '../../utilities/api'

import { CreatePackageModal } from './CreatePackageModal'

import AuthService from '../../utilities/services/auth.service'

// Create the context
const CreatePackageContext = createContext(undefined)

export const usePackageConfig = () => {
  const context = useContext(CreatePackageContext)
  if (!context) {
    throw new Error('Context must be used within a usePackageConfig')
  }
  return context
}

export const CreatePackageProvider = ({ children }) => {
  const navigate = useNavigate()

  const org = AuthService.getOrg()

  let organization

  if (org) organization = org[0]

  const [showPackage, setShowPackage] = useState(false)

  const [isLoading, setIsLoading] = useState(false)

  const [venues, setVenues] = useState()

  const [seasons, setSeasons] = useState()

  const [season, setSeason] = useState(null)

  const [seatmapId, setSeatmapId] = useState(null)

  const [room, setRoom] = useState({ uuid: '' })

  const [initialState, setInitialState] = useState()

  const [isSaving, setIsSaving] = useState(false)

  const [type, setType] = useState(null)

  const [flexPackName, setFlexPackName] = useState('')

  const [price, setPrice] = useState('')

  const [alert, setAlert] = useState({
    show: false,
    variant: '',
    message: '',
  })

  const types = [
    { name: 'Flex Package', key: 'flex-package' },
    { name: 'Season Package', key: 'season-package' },
  ]

  useEffect(() => {
    setIsLoading(true)
    if (AuthService.getOrg() == undefined) return
    getOrgVenues(organization?.uuid)
      .then((res) => {
        setVenues(res?.data)
      })
      .catch((err) => {
        setIsLoading(false)
        console.error(err)
      })

    setInitialState({ season, type, room, seatmapId, flexPackName, price })
  }, [])

  useEffect(() => {
    setSeatmapId(null)
  }, [room])

  useEffect(() => {
    console.log(type)
  }, [type])

  useEffect(() => {
    // Close alert if inputs change
    if (alert.show) {
      closeAlert()
    }
  }, [season, room, seatmapId, flexPackName, price])

  const closeAlert = () => {
    setAlert({
      show: false,
      variant: '',
      message: '',
    })
  }

  const loadVenues = () => {
    return getOrgVenues(organization?.uuid)
      .then((res) => {
        setVenues(res?.data)
        console.log('Venues: ', res?.data)
        setIsLoading(false)
      })
      .catch((err) => {
        console.error(err)
        setIsLoading(false)
      })
  }
  const loadSeasons = () => {
    return getSeasons(organization.uuid)
      .then((res) => {
        setIsLoading(false)
        setSeasons(res.data)
      })
      .catch((err) => {
        setIsLoading(false)
        console.error(err)
      })
  }

  const loadData = () => {
    setIsLoading(true)
    Promise.all([loadSeasons(), loadVenues()]) // ✅ execute + wait for both
      .then(() => {
        console.log('All data loaded!')
      })
      .catch((err) => {
        console.error('loadData error:', err)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleVenue = (e) => {
    const uuid = e.target.value
    const timezone = venues?.find((venue) =>
      venue.rooms.find((room) => room.uuid === uuid)
    )?.timezone
    const venue = venues?.find((venue) =>
      venue.rooms.find((room) => room.uuid === uuid)
    )
    const room = venue.rooms.find((room) => room.uuid === uuid)
    setRoom({ ...room, timezone })
  }

  const handleConfiguration = (e) => {
    setSeatmapId(e.target.value)
  }

  const handleClose = () => {
    setShowPackage(false)
  }

  const handlePackageShow = () => {
    setShowPackage(true)
    setRoom(initialState?.room)
    setSeatmapId(initialState?.seatmapId)
    setSeason(initialState?.season)
    setType(initialState?.type)
    setFlexPackName(initialState?.flexPackName)
    setPrice(initialState?.price)
  }

  const handleFlexPackName = (e) => {
    setFlexPackName(e.target.value)
  }

  const handleFlexPackPrice = (e) => {
    if (e?.target) setPrice(e.target.value)
  }

  const handleNext = () => {
    setIsSaving(true)
    if (type === 'flex-package') {
      let data = {}
      const formData = new FormData()
      const organization = AuthService.getOrg()[0]

      data['name'] = flexPackName
      data['season'] = season
      data['organization'] = organization.id
      data['organizationUUID'] = organization.uuid
      data['price'] = price
      // add venue to flex pack
      data['venue'] = seasons.find((s) => s.id === Number(season))?.venue?.id

      formData.append('data', JSON.stringify(data))
      createOrEditFlexPack(formData)
        .then((res) => {
          handleClose()
          setIsSaving(false)
          navigate(`/flex-package/${res?.data?.uuid}/flex-package-details`)
        })
        .catch((err) => {
          console.error(err)
          showAlert('flex-package')
        })
        .finally(() => setIsSaving(false))
    } else {
      createPackage({
        room,
        seatmapId,
        season,
        organizationUUID: AuthService.getOrg()?.[0]?.uuid,
      })
        .then((res) => {
          handleClose()
          navigate(`package/${res?.data?.uuid}/package-details`)
        })
        .catch((err) => {
          console.error(err)
          showAlert('package')
        })
        .finally(() => setIsSaving(false))
    }
  }

  const showAlert = (type) => {
    setAlert({
      show: true,
      variant: 'danger',
      message: `Unable to create ${type === 'flex-package' ? 'flex package' : 'season package'}. Please try again.`,
    })
  }

  return (
    <CreatePackageContext.Provider
      value={{
        showPackage,
        handleClose,
        handlePackageShow,
        seatmapId,
        room,
      }}
    >
      <CreatePackageModal
        showPackage={showPackage}
        loadData={loadData}
        isLoading={isLoading}
        venues={venues}
        room={room}
        seatmapId={seatmapId}
        handleVenue={handleVenue}
        handleConfiguration={handleConfiguration}
        handleClose={handleClose}
        handleNext={handleNext}
        isSaving={isSaving}
        seasons={seasons}
        setSeason={setSeason}
        season={season}
        type={type}
        setType={setType}
        types={types}
        flexPackName={flexPackName}
        handleFlexPackName={handleFlexPackName}
        handleFlexPackPrice={handleFlexPackPrice}
        price={price}
        alert={alert}
      />
      {children}
    </CreatePackageContext.Provider>
  )
}
